import * as store from '@/store';
import {TITLE_MAX_LENGTH, ERROR_TIP} from '@/constants/document/title';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {addParamToCustomUrl} from '@/utils/kuQuery';
import routers, {buildPath} from '@/config/routers';

import {
    DocumentUpdateData,
    Repository,
    WizDocument,
    DocumentOperateType, PathParams,
} from '@/interface';
import {
    isPrivateAndNoPower,
    canEditInKnowledge,
} from '@/domains/space/Repository/knowledgeUtils';
import {
    sideBarRootNode,
    TreeNodeData,
    ROOT_PARENT_GUID,
} from '@/domains/space/Repository/SidebarNew/type';
import sdk from '@/sdk';
import toast, {ToastType} from '@/components/Toast';
import {BOOK_TYPE, DOC_TYPE} from '@/constants/document/doc';
import {sourceType} from '@/constants/common';
import {DocRole, RepositoryScope, URL_SOURCE} from '@/Constant';

export enum MoreType {
    Copy = 'copy',
    Move = 'move',
    Rename = 'rename',
    Export = 'export',
    Delete = 'delete',
    ConvertToCatalog = 'convertToCatalog',
    ConvertToDoc = 'convertToDoc',
    AddCommonDoc = 'addCommonDoc',
    RemoveCommonDoc = 'removeCommonDoc',
    QuickMoveTop = 'quickMoveTop',
    QuickMoveBottom = 'quickMoveBottom',
}


export function getSlotList(list: sideBarRootNode[], id: string) {
    const rel: [sideBarRootNode[], sideBarRootNode[]] = [[], []];
    list.find((item, index) => {
        if (item.id === id) {
            rel[0].push(...list.slice(0, index + 1));
            rel[1].push(...list.slice(index + 1));
        }
        return item.id === id;
    });
    return rel;
}

export function getNodeCanEdit(node: any) {
    const isBook = node?.bookType === BOOK_TYPE.IS_BOOK;
    const inBook = node?.bookType === BOOK_TYPE.IN_BOOK;
    if ((isBook || inBook) && node?.canBookEdit) {
        return true;
    }
    const isDocAdmin = node?.docRoleName === DocRole.DocAdmin;
    const isDocMember = node?.docRoleName === DocRole.Member;

    const repository = store.getCurRepository();

    const isPrivate = !repository?.scope || RepositoryScope.Repository === repository?.scope;
    const isDirEdit =
        isPrivate && !repository?.roleName
            ? isBook && node?.permissions && node.permissions.includes('cd')
            : isBook && (isDocAdmin || isDocMember);
    const isDirDocEdit = (isDocAdmin || isDocMember) && inBook;
    const isRepoEdit = canEditInKnowledge(repository, node?.bookType, node?.inheritRepo);
    return isRepoEdit || isDirEdit || isDirDocEdit;
}

export async function docRename(
    docGuid: string,
    title: string
) {
    try {
        const curDocument = store.getCurDocument();
        const ksServer = await sdk.getKsServer();
        // const newDoc: DocumentUpdateData = {
        //     action: DocumentOperateType.Rename,
        //     name: title.slice(0, TITLE_MAX_LENGTH),
        // };
        // const newDocument = await ksServer.updateDocument(docGuid, newDoc);

        // MARKS: action = rename改为单独接口
        const titleName = title.slice(0, TITLE_MAX_LENGTH);
        const newDocument = await ksServer.renameDocument(docGuid, titleName);
        // 表格和知识本暂不具备通知能力，需要自己手动同步
        if (
            newDocument.type === DOC_TYPE.SHEET
            || newDocument.type === DOC_TYPE.DIRECTORY
            || newDocument.type === DOC_TYPE.PPT
            || newDocument.type === DOC_TYPE.DATASHEET
        ) {
            const {name, docGuid} = newDocument;
            // 侧边栏标题：标题重名时服务端会加上数字，需要二次同步
            if (titleName !== name) {
                const updateData: DocumentUpdateData = {
                    action: DocumentOperateType.Rename,
                    name,
                };
                ksServer.updateDocumentOperate(updateData, docGuid);
            }
            // 文档标题重命名知识本同步更新curDocument
            if (curDocument?.docGuid === docGuid) {
                store.setCurDocument({...curDocument, ...newDocument});
            }
            return name;
        }
        return newDocument?.name;
    } catch (err) {
        toast({
            content: ERROR_TIP,
            type: ToastType.Error,
        }).catch(err => {
            console.log(err);
        });
        throw err;
    }
}

// 获取节点信息
export async function getDocumentInfo(repository: Repository, docGuid: string, childGuid: string) {
    if (docGuid === ROOT_PARENT_GUID) {
        return null;
    }
    try {
        const ksServer = await sdk.getKsServer(repository);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        const doc = await ksServer.getDocumentInfoAccess(docGuid, childGuid);
        return doc;
    } catch (error) {
    // console.log(error);
    }
    return null;
}

export function getTreeNodeData(item: any, data: any, isBaikeRoute?: boolean, isBaikeRepo?: boolean) {
    const params: PathParams = {
        spaceGuid: data.spaceGuid || '',
        groupGuid: data.groupGuid || '',
        repositoryGuid: item.repositoryGuid || '',
        docGuid: item.docGuid,
        name: encodeURIComponent(item.name || ''),
    };
    if (isBaikeRoute === undefined) {
        isBaikeRoute = !!store.getData(store.KEYS.IS_BAIKE_ROUTE);
    }
    if (isBaikeRepo === undefined) {
        isBaikeRepo = !!store.getData(store.KEYS.IS_BAIKE_REPO);
    }
    let url = buildPath(isBaikeRoute ? routers.baike.entity : routers.Knowledge.path, params);
    function getSpliceUrl(key: any) {
        return addParamToCustomUrl(
            `${window.location.origin}${url}`,
            KU_QUERY.space.source,
            key).path;
    }
    if (isBaikeRepo) {
        url = getSpliceUrl(sourceType[107]);
    } else if (isPrivateAndNoPower()) {
        url = getSpliceUrl(URL_SOURCE.privateNoPowerSideBarDocs);
    } else {
        url = getSpliceUrl(URL_SOURCE.sideBarDocs);
    }
    Object.assign(item, {
        id: item.docGuid,
        key: item.docGuid,
        isLeaf: !item.childCount,
        text: item.name,
        url,
        spaceGuid: data.spaceGuid,
        groupGuid: data.groupGuid,
    });
    return item;
}

// 处理接口数据成使其能应用到数组件里
/**
 * @params result 请求回来的数据
 */
export function getTreeNodeListData(result: any[], data: {
    spaceGuid: string;
    groupGuid: string;
}) {
    const isBaikeRoute = !!store.getData(store.KEYS.IS_BAIKE_ROUTE);
    const isBaikeRepo = !!store.getData(store.KEYS.IS_BAIKE_REPO);
    result.forEach(item => {
        getTreeNodeData(item, data, isBaikeRoute, isBaikeRepo);
    });
    const docsInMeeting = store.getData(store.KEYS.DOCS_IN_MEETING) || [];
    const directoryEdit = store.getData(store.KEYS.DIRECTORY_EDIT) || [];
    (docsInMeeting.length || directoryEdit.length) && result.forEach(item => {
        if (docsInMeeting.includes(item.docGuid)) {
            item.isMeeting = true;
        }
        if (item.rootGuid && directoryEdit.includes(item.rootGuid)) {
            item.canBookEdit = true;
        }
    });
}

// 根据id获取tree数据中对应的节点信息
export function getNodeById(treeData: any[], docGuid: string) {
    let nodes = [...treeData];
    let current = null;
    // eslint-disable-next-line no-cond-assign
    while (current = nodes.shift()) {
        if (!current) {
            return null;
        }
        if (current.docGuid === docGuid) {
            return current;
        }
        current.children && (nodes = nodes.concat(current.children));
        if (nodes.length === 0) {
            return null;
        }
    }
    return null;
}

// 批量根据多个id获取tree数据中对应的多个节点信息
export function getNodesByIds(treeData: any[], docGuids: string[], keyName: string = 'docGuid') {
    if (!docGuids.length) {
        return [];
    }
    const result = [];
    let nodes = [...treeData];
    let current = null;
    // eslint-disable-next-line no-cond-assign
    while (current = nodes.shift()) {
        if (!current) {
            return result;
        }
        if (current[keyName] && docGuids.includes(current[keyName])) {
            result.push(current);
        }
        if (result.length === docGuids.length && keyName !== 'rootGuid') {
            return result;
        }
        current.children && (nodes = nodes.concat(current.children));
        if (nodes.length === 0) {
            return result;
        }
    }
    return result;
}

export async function getDocuments(
    repository: Repository,
    parentId?: string
) {
    let docs: WizDocument[] = [];
    try {
        const ksServer = await sdk.getKsServer(repository);
        docs = await ksServer.getDocumentsNew({
            parentGuid: `${parentId || ''}`,
        });
        // 修复数据
        const {
            repositoryGuid,
            spaceGuid,
            groupGuid,
        } = repository;
        docs.forEach(node => {
            Object.assign(node, {
                repositoryGuid,
                spaceGuid,
                groupGuid,
            });
        }, []);
    } catch (err) {
        throw err;
    }
    return docs;
}

// 获取节点下的子节点列表
// 子节点文章获取
export async function getChildNodesDataById(
    repository: Repository,
    parentId: string
) {
    try {
        const docs = await getDocuments(repository, parentId);
        getTreeNodeListData(docs, {
            spaceGuid: repository.spaceGuid,
            groupGuid: repository.groupGuid,
        });
        return docs;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

// 合并新列表到旧列表并使新列表的每项继承老列表的children
export function mergeChildrenList(newList: any[], oldList: any[]) {
    const oldChildrenObj = oldList.reduce((rel, item) => {
        rel[item.docGuid] = item.children;
        return rel;
    }, {});
    newList.forEach(item => {
        const children = oldChildrenObj[item.docGuid];
        if (children) {
            item.children = children;
        }
    });
}

// 从接口获取数据并更新当前节点的children
export async function updateChildNodes(
    repository: Repository,
    parentGuid: string = '',
    params: {
        treeData?: any;
        setTreeData?: any;
        oldChildren?: any[]; // 允许传入自己拼好的带有原本引用的list
    } = {}
) {
    let treeData = params.treeData;
    let setTreeData = params.setTreeData;
    if (!treeData) {
        const treeObj = store.getSidebarTreeObj();
        treeData = treeObj.value;
        setTreeData = treeObj.setValue;
    }
    // thisNode存在则表示当前节点是存在的
    // 不存在则表示这个节点的父节点不在已渲染的树上
    // 不存在的话有两种情况要么节点过深要么在根节点上层
    // 当前情况下应该只有在根节点上层这种情况
    // 从接口获取当前节点的信息
    const parentId = parentGuid && parentGuid !== ROOT_PARENT_GUID ? parentGuid : '';
    try {
        const newChildrenList = await getChildNodesDataById(repository, parentId);
        const thisNode = parentId ? getNodeById(treeData, parentId) : null;
        if (!thisNode) {
            mergeChildrenList(newChildrenList, treeData);
            setTreeData && setTreeData(newChildrenList);
            return newChildrenList;
        }
        const oldChildrenList = params.oldChildren || thisNode.children || [];
        if (!newChildrenList || newChildrenList.length === 0) {
            delete thisNode.children;
            thisNode.childCount = 0;
            thisNode.isLeaf = true;
            setTreeData && setTreeData([...treeData]);
        } else {
            thisNode.childCount = newChildrenList.length;
            mergeChildrenList(newChildrenList, oldChildrenList);
            thisNode.children = newChildrenList;
            thisNode.isLeaf = false;
            setTreeData && setTreeData([...treeData]);
        }
        return treeData;
    } catch (err) {
        console.log(err);
        return treeData;
    }
}

// 删除节点之后更新子节点
export async function updateChildNodesAfterDel(
    repository: any,
    curDocParentNode: any,
    curDocNode: any
) {
    // 当前节点被删除之后子节点会拼到父节点上
    const curDocParentNodeChildren = curDocParentNode && (curDocParentNode.children || []);
    const curDocNodeChildren = curDocNode && (curDocNode.children || []);
    const oldChildren = [...curDocParentNodeChildren, ...curDocNodeChildren];
    return updateChildNodes(repository, curDocParentNode.docGuid, {oldChildren: oldChildren});
}

export function getNextNode(docGuid: string = '', thisChildrenList: any[] = []): null | TreeNodeData {
    let relNode = null;
    thisChildrenList.find((item, index) => {
        if (item.docGuid === docGuid) {
            relNode = thisChildrenList[index + 1];
        }
        return item.docGuid === 'docGuid';
    });
    return relNode;
}
export function getPreNode(docGuid: string = '', thisChildrenList: any[] = []): null | TreeNodeData {
    let relNode = null;
    thisChildrenList.find((item, index) => {
        if (item.docGuid === docGuid) {
            relNode = thisChildrenList[index - 1];
        }
        return item.docGuid === 'docGuid';
    });
    return relNode;
}

export function delTreeNodeByDrag(treeData: any[], dragNode: any) {
    const parentGuid = dragNode.parent?.node?.docGuid || '';
    const parent = parentGuid ? getNodeById(treeData, parentGuid) : null;
    const children = parent ? parent.children : treeData;
    if (!children?.length) {
        return;
    }
    const index = children.findIndex((item: any) => item.docGuid === dragNode.docGuid);
    if (index === -1) {
        return;
    }
    children.splice(index, 1);
    if (parent) {
        parent.childCount = children.length;
        parent.isLeaf = !children.length;
        return parent.isLeaf; // 删除拖拽节点后，原父节点是否是叶子节点
    } else {
        return false; // 如果是根目录 return false
    }
}

export function addTreeNodeByDrag(treeData: any[], newNode: any, dropNode: any, position: number) {
    if (position === 0) {
        const node = getNodeById(treeData, dropNode.docGuid);
        if (!node.children) {
            node.children = [];
        }
        node.children.unshift(newNode);
        node.childCount = node.children.length;
        node.isLeaf = false;
        return node.children;
    } else {
        const parentGuid = dropNode.parent?.node?.docGuid || '';
        const parent = parentGuid ? getNodeById(treeData, parentGuid) : null;
        const children = parent ? parent.children : treeData;
        const index = children.findIndex((item: any) => item.docGuid === dropNode.docGuid);
        if (index === -1) {
            return children;
        }
        if (position === -1) {
            children.splice(index, 0, newNode);
        } else if (position === 1) {
            children.splice(index + 1, 0, newNode);
        }
        if (parent) {
            parent.childCount = children.length;
            parent.isLeaf = !children.length;
        }
        return children;
    }
}
// 判断两个简单数据类型的数组值是否一致
// 判断思路拼接两个数据然后看是否每个值都出现两次
export function isSameArr(arr1: any[], arr2: any[]) {
    if (arr1.length !== arr2.length) {
        return false;
    }
    const rel: {[key: string]: any} = {};
    const newArr = [...arr1, ...arr2];
    newArr.forEach(item => {
        const thisVal = rel[item];
        if (thisVal) {
            rel[item]++;
        } else {
            rel[item] = 1;
        }
        rel[item] === 2 && delete rel[item];
    });
    return Object.keys(rel).length === 0;
}

export async function getBookDirData(repository: Repository | null) {
    if (!repository) {
        return;
    }
    try {
        store.setDirectoryhasData(false);
        const ksServer = await sdk.getKsServer(repository);
        const result = await ksServer.getBookDirData(repository.repositoryGuid);
        store.setDirectoryhasData(true);
        if (result.books) {
            store.setDirectoryEdit(result.books);
        }
    } catch (error) {
        console.log(error);
    }
}

// 获取元素的所有后代元素（子元素，孙子元素等）
export function getPosterityNodes(node: any) {
    if (!node.children) {
        return [];
    }
    let children = [...node.children];
    let current = null;
    const rel = [];
    // eslint-disable-next-line no-cond-assign
    while (current = children.shift()) {
        if (current) {
            rel.push(current);
        }
        current.children && (children = children.concat(current.children));
        if (children.length === 0) {
            return rel;
        }
    }
    return rel;
}

export const addBlur = (className: string, closeMenu: any) => {
    const menuDom: HTMLDivElement | null = document.querySelector(className);
    if (menuDom) {
        menuDom.setAttribute('tabindex', '0');
        menuDom.focus();
        menuDom.removeEventListener('blur', closeMenu);
        menuDom.addEventListener('blur', closeMenu);
    }
};
